import { InfoIcon } from '@chakra-ui/icons';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Select,
  Switch,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import ReactSelect from 'react-select';
import PageLoader from '../../../components/layout/page-loader/PageLoader';
import { useCustomFields } from '../../../hooks/useCustomFields';
import { createPractice } from '../../../services/practices/createPractice';
import { getPracticeById } from '../../../services/practices/getPracticeById';
import { postOfficeHoursList } from '../../../services/practices/postOfficeHoursList';
import { updatePracticeById } from '../../../services/practices/updatePracticeById';
import { CustomField } from '../../../types/custom-fields/types';
import {
  CustomFieldValue,
  OfficeHour,
  OfficeHoursInput,
  Practice,
} from '../../../types/practices';
import {
  isRootFieldUnchanged,
  pruneUnchangedCustomFields,
} from '../../../utils/customFieldDiff';
import {
  LAB_IMAGING_DETAIL_KEY,
  LAB_IMAGING_OPTIONS,
  getClientIdWithFallback,
  getLabImagingValue,
} from '../../../utils/customFieldUtils';
import { officeHours } from '../../../utils/officeHours';
import { fieldLabels } from '../../../utils/practiceFieldMappings';
import { scrollToTop } from '../../../utils/scrollUtils';
import { stateOptions } from '../../../utils/stateOptions';
import { timeZoneOptions } from '../../../utils/timeZoneOptions';
// mapOfficeHoursToForm is now imported as newMapOfficeHoursToForm from utils/officeHoursProcessing.ts
import { useOfficeHours } from '../../../hooks/useOfficeHours';
import {
  debugOfficeHours,
  extractLunchHours,
  mapOfficeHoursToForm as newMapOfficeHoursToForm,
} from '../../../utils/officeHoursProcessing';
import { formatValidationErrors } from '../../../utils/officeHoursValidation';
import { to12h, to24h } from '../../../utils/timeConversion';
import OfficeHoursInputComponent from './OfficeHoursInputSimple';
import PracticeStatusBox from './PracticeStatusBox';

/** Custom fields answered Yes / No / Other, with free text behind "Other". */
const LAB_IMAGING_FIELDS = [
  'Labs-Drawn-Hours',
  'Imaging-Available-Hours',
];

/** UI-only form key holding the "Other" free text for a lab/imaging field. */
const labImagingDetailKey = (fieldName: string) =>
  `${fieldName}-Detail`;

//type FormDataObject = Record<string, string | boolean | string[]>;
interface PracticeFormValues {
  [key: string]:
    | string
    | boolean
    | string[]
    | undefined
    | OfficeHoursInput[];
  officeHoursInput?: OfficeHoursInput[];
}

interface MarketOption {
  label: string;
  value: string;
}

function isPrimitiveField(
  value: unknown
): value is string | boolean | string[] {
  return (
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    (Array.isArray(value) &&
      value.every((item) => typeof item === 'string'))
  );
}

function getInitialFormValues(
  practiceData: Practice | undefined,
  customFields: CustomField[]
): Record<string, unknown> {
  const initialValues: PracticeFormValues = {} as PracticeFormValues;
  if (!practiceData) return initialValues;

  const rootFields: (keyof Practice)[] = [
    'name',
    'address1',
    'address2',
    'city',
    'state',
    'zip',
    'market',
    'timezone',
    'observesDaylightSavingsTime',
    'active',
  ];
  const statesData: { markets: { id: string; name: string }[] }[] =
    JSON.parse(localStorage.getItem('statesData') || '[]');

  const marketOptions: MarketOption[] = statesData
    .reduce(
      (
        acc: MarketOption[],
        state: { markets: { id: string; name: string }[] }
      ) => {
        return acc.concat(
          state.markets.map(
            (market: { id: string; name: string }) => ({
              label: market.name,
              value: market.id,
            })
          )
        );
      },
      []
    )
    .sort((a, b) => a.label.localeCompare(b.label));

  rootFields.forEach((field) => {
    let value = practiceData[field];

    // Handle special cases for normalization
    if (field === 'market' && typeof value === 'string') {
      // If market is a name, find the corresponding ID
      const matchingMarket = marketOptions.find(
        (market: MarketOption) => market.label === value
      );
      if (matchingMarket) {
        value = matchingMarket.value;
      }
    }

    // Normalize empty values
    if (
      value === null ||
      value === undefined ||
      value === 'nan' ||
      value === ''
    ) {
      if (
        field === 'address2' ||
        field === 'city' ||
        field === 'zip' ||
        field === 'timezone'
      ) {
        // These fields can be empty strings in the UI
        value = '';
      } else if (field === 'observesDaylightSavingsTime') {
        // Boolean field defaults to false
        value = false;
      } else if (field === 'active') {
        // Boolean field defaults to true
        value = true;
      } else {
        // Other fields default to empty string
        value = '';
      }
    }

    if (isPrimitiveField(value)) {
      initialValues[field] = value;
    }
  });

  // Custom fields
  customFields.forEach((cf) => {
    const fieldData = practiceData.customFields?.[cf.name];

    if (LAB_IMAGING_FIELDS.includes(cf.name)) {
      const { status, detail } = getLabImagingValue(
        practiceData.customFields,
        cf.name
      );
      initialValues[cf.name] = status;
      initialValues[labImagingDetailKey(cf.name)] = detail;
      return;
    }

    if (Array.isArray(fieldData) && fieldData.length > 0) {
      const value = fieldData[0]?.value;
      if (value !== undefined && value !== null && value !== 'nan') {
        // Normalize empty custom field values
        if (value === '') {
          initialValues[cf.name] = '';
        } else {
          initialValues[cf.name] = value;
        }
      } else {
        // Set empty string for null/undefined/nan values
        initialValues[cf.name] = '';
      }
    } else {
      // Set empty string for missing custom fields
      initialValues[cf.name] = '';
    }
  });

  return initialValues;
}

// Time conversion helpers are now imported from utils/timeConversion.ts

// Office Hours Preview Component
interface OfficeHoursPreviewProps {
  officeHoursInput: OfficeHoursInput[];
  lunchHours: {
    start: string;
    end: string;
    startDisplay: string;
    endDisplay: string;
  };
  observesLunchHours: boolean;
  timezone: string;
  state?: string;
}

const OfficeHoursPreview: React.FC<OfficeHoursPreviewProps> = ({
  officeHoursInput,
  lunchHours,
  observesLunchHours,
  timezone,
  state,
}) => {
  // Convert form data to the format expected by PracticeStatusBox
  const convertToOfficeHours = (): OfficeHour[] => {
    const result: OfficeHour[] = [];

    officeHoursInput.forEach((day, index) => {
      const dayOfWeek = index + 1; // Convert 0-based index to 1-based day of week

      if (day.closed || !day.open || !day.close) {
        // Closed day
        result.push({
          id: `preview-${dayOfWeek}`,
          practice_id: 'preview',
          day_of_week: dayOfWeek,
          open_time: null,
          close_time: null,
          closed: true,
          end_of_day: false,
          keep_taking_calls: null,
          override_values: null,
        });
      } else if (
        observesLunchHours &&
        lunchHours.start &&
        lunchHours.end
      ) {
        // Split day with lobby closure
        const openTime = day.open;
        const closeTime = day.close;
        const lobbyStart = lunchHours.start;
        const lobbyEnd = lunchHours.end;

        // Check if lobby closure fits within office hours
        if (
          openTime < lobbyStart &&
          lobbyStart < lobbyEnd &&
          lobbyEnd < closeTime
        ) {
          // Morning block
          result.push({
            id: `preview-${dayOfWeek}-morning`,
            practice_id: 'preview',
            day_of_week: dayOfWeek,
            open_time: openTime,
            close_time: lobbyStart,
            closed: false,
            end_of_day: false,
            keep_taking_calls: null,
            override_values: null,
          });

          // Afternoon block
          result.push({
            id: `preview-${dayOfWeek}-afternoon`,
            practice_id: 'preview',
            day_of_week: dayOfWeek,
            open_time: lobbyEnd,
            close_time: closeTime,
            closed: false,
            end_of_day: true,
            keep_taking_calls: null,
            override_values: null,
          });
        } else {
          // Single block (lobby closure doesn't fit)
          result.push({
            id: `preview-${dayOfWeek}`,
            practice_id: 'preview',
            day_of_week: dayOfWeek,
            open_time: openTime,
            close_time: closeTime,
            closed: false,
            end_of_day: false,
            keep_taking_calls: null,
            override_values: null,
          });
        }
      } else {
        // Single block without lobby closure
        result.push({
          id: `preview-${dayOfWeek}`,
          practice_id: 'preview',
          day_of_week: dayOfWeek,
          open_time: day.open,
          close_time: day.close,
          closed: false,
          end_of_day: false,
          keep_taking_calls: null,
          override_values: null,
        });
      }
    });

    return result;
  };

  const officeHours = convertToOfficeHours();

  return (
    <Box>
      <PracticeStatusBox
        timezone={timezone || 'EST'}
        unFormattedOfficeHours={officeHours}
        isLoading={false}
        state={state || ''}
      />
    </Box>
  );
};


// Generate time options in 15-minute increments for lobby closure hours
const generateLobbyClosureTimeOptions = () => {
  const times: Array<{ label: string; value: string }> = [];

  // Add 12:00 AM to 11:45 AM
  for (let hour = 0; hour <= 11; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const meridiem = 'AM';
      const displayHour = hour === 0 ? 12 : hour;
      const timeString = `${displayHour}:${minute
        .toString()
        .padStart(2, '0')} ${meridiem}`;
      times.push({
        label: timeString,
        value: timeString,
      });
    }
  }

  // Add 12:00 PM to 11:45 PM
  for (let hour = 12; hour <= 23; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const meridiem = 'PM';
      const displayHour = hour === 12 ? 12 : hour - 12;
      const timeString = `${displayHour}:${minute
        .toString()
        .padStart(2, '0')} ${meridiem}`;
      times.push({
        label: timeString,
        value: timeString,
      });
    }
  }

  return times;
};

const lobbyClosureTimeOptions = generateLobbyClosureTimeOptions();

// Unit test assertions (console only)
if (import.meta.env.DEV) {
  console.assert(
    to24h('12:00 AM') === '00:00',
    'Midnight AM should be 00:00'
  );
  console.assert(
    to24h('12:00 PM') === '12:00',
    'Noon PM should be 12:00'
  );
  console.assert(
    to24h('1:30 PM') === '13:30',
    '1:30 PM should be 13:30'
  );
  console.assert(
    to24h('11:45 PM') === '23:45',
    '11:45 PM should be 23:45'
  );
  console.assert(
    to24h('9:15 AM') === '09:15',
    '9:15 AM should be 09:15'
  );

  console.assert(
    to12h('00:00') === '12:00 AM',
    '00:00 should be 12:00 AM'
  );
  console.assert(
    to12h('12:00') === '12:00 PM',
    '12:00 should be 12:00 PM'
  );
  console.assert(
    to12h('13:30') === '1:30 PM',
    '13:30 should be 1:30 PM'
  );
  console.assert(
    to12h('23:45') === '11:45 PM',
    '23:45 should be 11:45 PM'
  );
  console.assert(
    to12h('09:15') === '9:15 AM',
    '09:15 should be 9:15 AM'
  );
}

const CreatePractice = () => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    getValues,
    reset,
  } = useForm<PracticeFormValues>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const { id: practiceId } = useParams<{ id: string }>();
  const isEditMode = !!practiceId;

  // State for fetched data
  const [practiceData, setPracticeData] = useState<Practice | null>(
    null
  );
  const [officeHoursData, setOfficeHoursData] = useState<
    OfficeHour[]
  >([]);
  const [practiceLoading, setPracticeLoading] = useState(false);
  const [practiceError, setPracticeError] = useState<string | null>(
    null
  );

  const [hasInitializedOfficeHours, setHasInitializedOfficeHours] =
    useState(false);
  const toast = useToast();
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const cancelDialog = useDisclosure();
  const cancelRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use the new office hours hook for state management
  const {
    officeHoursInput,
    observesLunchHours,
    lunchHours,
    validation: officeHoursValidation,
    setOfficeHoursInput,
    setObservesLunchHours,
    setLunchHours,
    saveOfficeHours,
    isValid: isOfficeHoursValid,
  } = useOfficeHours();

  const navigate = useNavigate();
  const statesData: { markets: { id: string; name: string }[] }[] =
    JSON.parse(localStorage.getItem('statesData') || '[]');

  const marketOptions: MarketOption[] = statesData
    .reduce(
      (
        acc: MarketOption[],
        state: { markets: { id: string; name: string }[] }
      ) => {
        return acc.concat(
          state.markets.map(
            (market: { id: string; name: string }) => ({
              label: market.name,
              value: market.id,
            })
          )
        );
      },
      []
    )
    .sort((a, b) => a.label.localeCompare(b.label));
  // Get clientId for custom fields
  const clientId = getClientIdWithFallback();

  // Use custom hook to fetch custom fields
  const {
    customFields: customFieldsFromHook,
    loading: customFieldsLoading,
  } = useCustomFields(clientId, 'Practice');

  useEffect(() => {
    if (customFieldsFromHook.length > 0) {
      setCustomFields(customFieldsFromHook);
      setLoading(false);
    }
  }, [customFieldsFromHook]);

  useEffect(() => {
    if (customFieldsLoading) {
      setLoading(true);
    }
  }, [customFieldsLoading]);

  const fetchPracticeData = useCallback(async () => {
    if (!practiceId) return;

    setPracticeLoading(true);
    setPracticeError(null);

    try {
      const response = await getPracticeById(practiceId);
      const practiceDetails = response.data;

      if (practiceDetails?.practice) {
        setPracticeData(practiceDetails.practice);

        // Fetch office hours if practice exists
        try {
          const officeHoursResponse = await postOfficeHoursList([
            practiceDetails.practice.id,
          ]);
          setOfficeHoursData(
            officeHoursResponse.data.office_hours || []
          );
        } catch (officeHoursError) {
          console.error(
            'Failed to fetch office hours:',
            officeHoursError
          );
          setOfficeHoursData([]);
        }
      } else {
        setPracticeError('Practice not found');
      }
    } catch (error) {
      console.error('Failed to fetch practice:', error);
      setPracticeError(
        'Failed to load practice data. Please try again.'
      );
      toast({
        title: 'Error loading practice',
        description:
          'Failed to load practice data. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPracticeLoading(false);
    }
  }, [practiceId, toast]);

  // Fetch practice data when in edit mode
  useEffect(() => {
    if (isEditMode && practiceId) {
      fetchPracticeData();
    }
  }, [fetchPracticeData, isEditMode, practiceId]);

  // Function to filter out unsupported fields
  const filterUnsupportedFields = (
    formData: PracticeFormValues
  ): PracticeFormValues => {
    const omittedFields = ['officeHoursInput'];

    return Object.keys(formData).reduce((filteredData, key) => {
      if (!omittedFields.includes(key)) {
        filteredData[key as keyof PracticeFormValues] = formData[key];
      }
      return filteredData;
    }, {} as PracticeFormValues);
  };

  // Function to transform the JSON into the API format
  const transformPracticeData = (
    formData: PracticeFormValues,
    customFieldsList: CustomField[],
    practiceId?: string
  ) => {
    const OMIT_CUSTOM_FIELDS = [
      'Make-Enabled',
      'Reschedule-Enabled',
      'Confirm-Enabled',
      'Cancel-Enabled',
      'Athena-Department',
      'Athena-Department-Link',
    ];
    const rootFields = [
      'name',
      'address1',
      'address2',
      'city',
      'state',
      'zip',
      'market',
      'timezone',
      'observesDaylightSavingsTime',
      'active',
    ];

    const transformedData: Record<string, unknown | null> = {};
    const storedPractice = practiceData as unknown as
      | Record<string, unknown>
      | null;

    rootFields.forEach((field) => {
      if (formData[field] !== undefined) {
        const value = formData[field];
        let outgoingValue: unknown;

        // Denormalize empty values for API
        if (value === '' || value === null || value === undefined) {
          if (field === 'observesDaylightSavingsTime') {
            // Boolean field defaults to false
            outgoingValue = false;
          } else if (field === 'active') {
            // Boolean field defaults to true
            outgoingValue = true;
          } else {
            // All other fields default to empty string for consistency
            outgoingValue = '';
          }
        } else {
          outgoingValue = value;
        }

        // In edit mode only send root fields the user actually changed
        if (
          isEditMode &&
          storedPractice &&
          isRootFieldUnchanged(outgoingValue, storedPractice[field])
        ) {
          return;
        }

        transformedData[field] = outgoingValue;
      }
    });

    if (
      !isEditMode ||
      !isRootFieldUnchanged(formData.timezone, storedPractice?.timezone)
    ) {
      transformedData.timezone = formData.timezone;
    }

    if (!isEditMode) {
      transformedData.startDate = new Date()
        .toISOString()
        .split('T')[0];
    }

    const customFields: Record<string, CustomFieldValue[]> = {};

    customFieldsList.forEach((cf) => {
      const fieldKey = cf.name;
      const fieldValue = formData[fieldKey];
      if (OMIT_CUSTOM_FIELDS.includes(fieldKey)) {
        return;
      }

      // Define form fields that should always be sent, even when empty
      const FORM_FIELDS = [
        // Location Section
        'Directions',
        'Group-TID',

        // Medical Services
        'Labs-Drawn-Hours',
        'Imaging-Available-Hours',
        'Next-In-Line',

        // Contact Section
        'Phone',
        'Fax',
        'Medical-Records-Number',
        'Backline',
        'Manager-Name',
        'Manager-Line',
        'Manager-Email',
        'After-Hours',
        'Clinical-Staff',
        'Front-Staff',
        'Director',

        // Cerner Section
        'Cerner-pool',
        'Cerner-location',
        'Cerner-Hub',

        // Other Form Fields
        'Screen-For-Covid-When-Scheduling',
        'Flu-Shots',
        'Ekg',
        'Clinic NPI',
      ];

      // Skip empty values only for non-form fields
      // This prevents sending empty fields that aren't part of our form while allowing form fields to be cleared
      if (
        fieldValue !== undefined &&
        (fieldValue !== '' || FORM_FIELDS.includes(fieldKey))
      ) {
        const existingField = isEditMode
          ? (() => {
              const fv = practiceData?.customFields?.[fieldKey];
              return Array.isArray(fv) ? fv[0] : null;
            })()
          : null;

        const value = Array.isArray(fieldValue)
          ? (fieldValue as string[]).join(', ')
          : String(fieldValue);
        // Keep the stored metadata as-is (null must stay null) so an
        // untouched field is not rewritten on save
        let metadata =
          (existingField?.metadata as
            | Record<string, string | string[] | null>
            | undefined) ?? (isEditMode ? null : {});

        // Yes/No/Other fields keep the "Other" free text in metadata;
        // only touch it when the detail actually changes so unchanged
        // rows are still pruned from the payload.
        if (LAB_IMAGING_FIELDS.includes(fieldKey)) {
          const detail =
            value === 'Other'
              ? String(
                  formData[labImagingDetailKey(fieldKey)] ?? ''
                ).trim() || null
              : null;
          const storedDetail =
            metadata?.[LAB_IMAGING_DETAIL_KEY] ?? null;
          if (detail !== storedDetail) {
            metadata = {
              ...(metadata ?? {}),
              [LAB_IMAGING_DETAIL_KEY]: detail,
            };
          }
        }

        customFields[fieldKey] = [
          {
            fieldId: cf.id,
            value,
            startDate: existingField?.startDate || null,
            endDate: existingField?.endDate || null,
            metadata: metadata as CustomFieldValue['metadata'],
            rowId:
              existingField?.rowId ||
              (isEditMode && practiceId ? practiceId : null), // Use existing rowId or practice ID in edit mode
            ...(isEditMode && existingField?.id
              ? { id: existingField.id }
              : {}), // Only include id in edit mode if it exists
          },
        ];
      }
    });

    // In edit mode only send custom fields the user actually changed
    transformedData.customFields = isEditMode
      ? pruneUnchangedCustomFields(
          customFields,
          practiceData?.customFields
        )
      : customFields;

    return transformedData;
  };

  // ---- Office hours helpers ----
  const validateOfficeHours = (): boolean => {
    if (!isOfficeHoursValid) {
      // Show validation errors
      const { errorMessages, warningMessages } =
        formatValidationErrors(officeHoursValidation);

      if (errorMessages.length > 0) {
        toast({
          title: 'Office Hours Validation Error',
          description: errorMessages.join('; '),
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
      }

      if (warningMessages.length > 0) {
        toast({
          title: 'Office Hours Warning',
          description: warningMessages.join('; '),
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
      }

      return false;
    }

    return true;
  };

  // Office hours processing is now handled by the useOfficeHours hook

  const getSafeSelectValue = (value: unknown) => {
    if (typeof value === 'boolean') return String(value);
    if (typeof value === 'string' || typeof value === 'number')
      return value;
    return '';
  };

  const onSubmit = async () => {
    if (!validateOfficeHours()) return; // Halt execution if validation fails

    const latestFormData = getValues();
    const filteredData = filterUnsupportedFields(latestFormData);
    const transformedData = transformPracticeData(
      filteredData,
      customFields,
      practiceId
    );

    try {
      setIsSubmitting(true);

      if (isEditMode) {
        // Update practice data
        await updatePracticeById(practiceId, transformedData);

        // Handle office hours using the new service
        const officeHoursResult = await saveOfficeHours(
          practiceId,
          officeHoursData,
          true
        );

        if (!officeHoursResult.success) {
          throw new Error(
            `Office hours update failed: ${officeHoursResult.errors.join(
              ', '
            )}`
          );
        }

        // Show warnings if any
        if (officeHoursResult.warnings.length > 0) {
          const warningMessages = officeHoursResult.warnings
            .map((warning) =>
              typeof warning === 'string'
                ? warning
                : warning?.message ?? ''
            )
            .filter((message) => message.trim().length > 0);

          if (warningMessages.length > 0) {
          toast({
            title: 'Office Hours Updated',
              description: warningMessages
                .map((warning) => `• ${warning}`)
                .join('\n'),
            status: 'warning',
            duration: 4000,
            isClosable: true,
          });
          }
        }

        // Dispatch custom event to notify other components (e.g., PracticeDetail) to refresh office hours
        window.dispatchEvent(
          new CustomEvent('officeHoursUpdated', {
            detail: { practiceId },
          })
        );
      } else {
        // Create new practice
        const response = await createPractice(transformedData);
        const newPracticeId = response.data?.practiceId || '';

        // Handle office hours using the new service
        const officeHoursResult = await saveOfficeHours(
          newPracticeId,
          [],
          false
        );

        if (!officeHoursResult.success) {
          throw new Error(
            `Office hours creation failed: ${officeHoursResult.errors.join(
              ', '
            )}`
          );
        }

        // Show warnings if any
        if (officeHoursResult.warnings.length > 0) {
          const warningMessages = officeHoursResult.warnings
            .map((warning) =>
              typeof warning === 'string'
                ? warning
                : warning?.message ?? ''
            )
            .filter((message) => message.trim().length > 0);

          if (warningMessages.length > 0) {
          toast({
            title: 'Practice Created',
              description: warningMessages
                .map((warning) => `• ${warning}`)
                .join('\n'),
            status: 'warning',
            duration: 4000,
            isClosable: true,
          });
          }
        }
      }

      // Smooth scroll to top on successful save
      scrollToTop('smooth', 800);

      toast({
        title: 'Practice saved successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate(`/practices/${isEditMode ? practiceId : ''}`);
    } catch (error) {
      console.error('Error saving practice:', error);
      toast({
        title: 'Error saving practice.',
        description:
          'An error occurred while saving the practice. Please try again.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isEditMode && practiceData && customFields.length > 0) {
      // Normalize the practice data for form initialization
      const normalizedValues = getInitialFormValues(
        practiceData,
        customFields
      );
      reset(normalizedValues as PracticeFormValues);
    }
  }, [isEditMode, practiceData, customFields, reset]);

  // Initialize office hours for edit mode
  useEffect(() => {
    if (
      !hasInitializedOfficeHours &&
      isEditMode &&
      officeHoursData?.length &&
      practiceData
    ) {
      try {
        // Use the proven mapping approach for reliability
        const mappedOfficeHours =
          newMapOfficeHoursToForm(officeHoursData);
        const extractedLunchHours =
          extractLunchHours(officeHoursData);

        // Set office hours input with reliable mapping
        setOfficeHoursInput(
          mappedOfficeHours.map((day) => ({
            ...day,
            openError: false,
            closeError: false,
          }))
        );

        // Set lobby closure hours based on extraction
        // Only set observesLunchHours to true if we can actually extract lunch hours from the data
        // Do not use detectMissingLobbyClosure heuristic as it causes false positives
        if (extractedLunchHours) {
          setObservesLunchHours(true);
          setLunchHours({
            start: extractedLunchHours.start,
            end: extractedLunchHours.end,
            startDisplay: to12h(extractedLunchHours.start),
            endDisplay: to12h(extractedLunchHours.end),
          });
        } else {
          // No lunch hours found in the data - set to false
          setObservesLunchHours(false);
          setLunchHours({
            start: '',
            end: '',
            startDisplay: '',
            endDisplay: '',
          });
        }

        // Debug logging
        debugOfficeHours('Edit Mode Initialization', {
          originalData: officeHoursData,
          mappedOfficeHours,
          extractedLunchHours,
        });

        setHasInitializedOfficeHours(true);
      } catch (error) {
        console.error(
          'Error initializing office hours for edit mode:',
          error
        );
        // Fallback to empty state if initialization fails
        setOfficeHoursInput(
          officeHours.map((day) => ({
            day,
            open: '',
            close: '',
            closed: true,
            openDisplay: '',
            closeDisplay: '',
            openError: false,
            closeError: false,
          }))
        );
        setHasInitializedOfficeHours(true);
      }
    }
  }, [
    isEditMode,
    officeHoursData,
    practiceData,
    hasInitializedOfficeHours,
    setOfficeHoursInput,
    setObservesLunchHours,
    setLunchHours,
  ]);

  if (loading || (isEditMode && practiceLoading))
    return <PageLoader />;

  // Show error state if practice data failed to load
  if (isEditMode && practiceError) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        height="50vh"
        gap="1rem"
      >
        <Text fontSize="1.25rem" fontWeight="600" color="red.500">
          {practiceError}
        </Text>
        <Button colorScheme="blue" onClick={fetchPracticeData}>
          Retry
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate('/practices')}
        >
          Back to Practices
        </Button>
      </Flex>
    );
  }

  // Show error if practice ID is invalid in edit mode
  if (isEditMode && !practiceId) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        height="50vh"
        gap="1rem"
      >
        <Text fontSize="1.25rem" fontWeight="600" color="red.500">
          Invalid practice ID
        </Text>
        <Button
          variant="outline"
          onClick={() => navigate('/practices')}
        >
          Back to Practices
        </Button>
      </Flex>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {isEditMode && practiceData?.name
            ? `Edit Practice - ${practiceData.name}`
            : isEditMode
            ? 'Edit Practice'
            : 'Create Practice'}
        </title>
      </Helmet>
      <Flex
        direction="column"
        align="flex-start"
        gap="1rem"
        width="60rem"
        p={6}
        mx="auto"
      >
        <Text
          fontSize="1.875rem"
          fontWeight="700"
          lineHeight={'133%'}
          color="text.primary"
          mb="2rem"
        >
          {isEditMode ? 'Edit Practice' : 'Add Practice'}
        </Text>

        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{ width: '100%' }}
        >
          <Flex direction="column" gap="1rem">
            {/* Identification Section */}
            <Text
              fontSize="1.25rem"
              fontWeight="700"
              lineHeight={'120%'}
              color="text.secondary"
              mb="1.5rem"
            >
              Identification
            </Text>
            {['name'].map((field) => (
              <FormControl key={field} isInvalid={!!errors[field]}>
                <FormLabel
                  fontSize={'0.75rem'}
                  fontWeight={500}
                  lineHeight={'1rem'}
                >
                  {fieldLabels[field]}*
                </FormLabel>
                <Input
                  {...register(field, {
                    required: `${fieldLabels[field]} is required`,
                  })}
                  placeholder={`Enter ${fieldLabels[field]}`}
                />
                <FormErrorMessage>
                  {errors[field]?.message as string}
                </FormErrorMessage>
              </FormControl>
            ))}

            <FormControl
              key="Friendly-Practice-Name"
              isInvalid={!!errors['Friendly-Practice-Name']}
            >
              <FormLabel
                fontSize={'0.75rem'}
                fontWeight={500}
                lineHeight={'1rem'}
              >
                Friendly Practice Name
              </FormLabel>
              <Input
                {...register('Friendly-Practice-Name')}
                placeholder="Enter Friendly Practice Name"
              />
              <FormErrorMessage>
                {errors['Friendly-Practice-Name']?.message as string}
              </FormErrorMessage>
            </FormControl>
            <FormControl
              key="Clinic NPI"
              isInvalid={!!errors['Clinic NPI']}
            >
              <FormLabel
                fontSize={'0.75rem'}
                fontWeight={500}
                lineHeight={'1rem'}
              >
                Clinic NPI
              </FormLabel>
              <Input
                {...register('Clinic NPI')}
                placeholder="Enter Clinic NPI"
              />
              <FormHelperText>
                National Provider Identifier for the clinic
              </FormHelperText>
              <FormErrorMessage>
                {errors['Clinic NPI']?.message as string}
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!errors.active}>
              <FormLabel
                fontSize={'0.75rem'}
                fontWeight={500}
                lineHeight={'1rem'}
              >
                Active Status*
              </FormLabel>
              <Controller
                name="active"
                control={control}
                rules={{ required: 'Active status is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select Active Status"
                    value={getSafeSelectValue(field.value)}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Select>
                )}
              />
              <FormErrorMessage>
                {errors.active?.message as string}
              </FormErrorMessage>
            </FormControl>

            {/* Location Section */}
            <Text
              fontSize="1.25rem"
              fontWeight="700"
              color="text.secondary"
            >
              Location
            </Text>
            {['address1'].map((field) => (
              <FormControl key={field} isInvalid={!!errors[field]}>
                <FormLabel
                  fontSize={'0.75rem'}
                  fontWeight={500}
                  lineHeight={'1rem'}
                >
                  {fieldLabels[field]}*
                </FormLabel>
                <Input
                  {...register(field, {
                    required: `${fieldLabels[field]} is required`,
                  })}
                  placeholder={`Enter ${fieldLabels[field]}`}
                />
                <FormErrorMessage>
                  {errors[field]?.message as string}
                </FormErrorMessage>
              </FormControl>
            ))}
            {['address2'].map((field) => (
              <FormControl key={field}>
                <FormLabel
                  fontSize={'0.75rem'}
                  fontWeight={500}
                  lineHeight={'1rem'}
                >
                  {fieldLabels[field]}
                </FormLabel>
                <Input
                  {...register(field, {})}
                  placeholder={`Enter ${fieldLabels[field]}`}
                />
              </FormControl>
            ))}
            {['city', 'zip'].map((field) => (
              <FormControl key={field} isInvalid={!!errors[field]}>
                <FormLabel
                  fontSize={'0.75rem'}
                  fontWeight={500}
                  lineHeight={'1rem'}
                >
                  {fieldLabels[field]}*
                </FormLabel>
                <Input
                  {...register(field, {
                    required: `${fieldLabels[field]} is required`,
                  })}
                  placeholder={`Enter ${fieldLabels[field]}`}
                />
                <FormErrorMessage>
                  {errors[field]?.message as string}
                </FormErrorMessage>
              </FormControl>
            ))}
            <FormControl isInvalid={!!errors.state}>
              <FormLabel
                fontSize={'0.75rem'}
                fontWeight={500}
                lineHeight={'1rem'}
              >
                {fieldLabels.state}*
              </FormLabel>
              <Controller
                name="state"
                control={control}
                rules={{ required: 'State is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select State"
                    value={getSafeSelectValue(field.value)}
                  >
                    {stateOptions.map((state) => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </Select>
                )}
              />
              <FormErrorMessage>
                {errors.state?.message as string}
              </FormErrorMessage>
            </FormControl>
            {['Directions'].map((field) => (
              <FormControl key={field}>
                <FormLabel
                  fontSize={'0.75rem'}
                  fontWeight={500}
                  lineHeight={'1rem'}
                >
                  {fieldLabels[field]}
                </FormLabel>
                <Input
                  {...register(field)}
                  placeholder={`Enter ${fieldLabels[field]}`}
                />
              </FormControl>
            ))}

            <FormControl isInvalid={!!errors.market}>
              <FormLabel
                fontSize={'0.75rem'}
                fontWeight={500}
                lineHeight={'1rem'}
              >
                Market*
              </FormLabel>
              <Controller
                name="market"
                control={control}
                rules={{ required: 'Market is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select Market"
                    value={getSafeSelectValue(field.value)}
                  >
                    {marketOptions.map(({ label, value }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                )}
              />
              <FormErrorMessage>
                {errors.market?.message as string}
              </FormErrorMessage>
            </FormControl>

            <FormControl
              key={'Group-TID'}
              isInvalid={!!errors['Group-TID']}
            >
              <FormLabel
                fontSize={'0.75rem'}
                fontWeight={500}
                lineHeight={'1rem'}
              >
                {fieldLabels['Group-TID']}
              </FormLabel>
              <Input
                {...register('Group-TID')}
                placeholder={`Enter ${fieldLabels['Group-TID']}`}
              />
            </FormControl>

            {/* Hours & Availability */}
            <Text
              fontSize="1.25rem"
              fontWeight="700"
              color="text.secondary"
            >
              Hours & Availability
            </Text>
            <FormControl isInvalid={!!errors.timezone}>
              <FormLabel
                fontSize={'0.75rem'}
                fontWeight={500}
                lineHeight={'1rem'}
              >
                Timezone*
              </FormLabel>
              <Controller
                name="timezone"
                control={control}
                rules={{ required: 'Timezone is required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select Timezone"
                    value={getSafeSelectValue(field.value)}
                  >
                    {' '}
                    {timeZoneOptions.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </Select>
                )}
              />
              <FormErrorMessage>
                {errors.timezone?.message as string}
              </FormErrorMessage>
            </FormControl>

            <FormControl
              display="flex"
              alignItems="center"
              key={'observesDaylightSavingsTime'}
            >
              <Checkbox
                {...register('observesDaylightSavingsTime')}
                defaultChecked={false}
              >
                Observes Daylight Savings?
              </Checkbox>
            </FormControl>

            {/* Office Hours UI Only */}
            <Text fontSize="1rem" fontWeight="600">
              Office Hours
            </Text>
            <FormControl>
              <FormHelperText fontSize="0.75rem" color="gray.600">
                Set office hours for each day of the week.
              </FormHelperText>
            </FormControl>
            {/* Lobby Closure Hours Input */}
            <Flex direction="column" gap="0.5rem">
              <Flex alignItems="center" gap="0.5rem">
                <Checkbox
                  isChecked={observesLunchHours}
                  onChange={(e) => {
                    setObservesLunchHours(e.target.checked);
                    if (!e.target.checked) {
                      // Clear lobby closure hours when unchecking
                      setLunchHours({
                        start: '',
                        end: '',
                        startDisplay: '',
                        endDisplay: '',
                      });
                      // Error handling now managed by validation
                    }
                  }}
                />
                <Text fontSize="0.875rem" fontWeight="500">
                  Practice observes lobby closure hours?
                </Text>
              </Flex>

              {observesLunchHours && (
                <>
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    fontStyle="italic"
                    mb="0.5rem"
                  >
                    When enabled, lobby closure periods will be
                    tracked during office hours.
                  </Text>

                  <Flex
                    alignItems="center"
                    gap="1rem"
                    py="0.5rem"
                    px="0.75rem"
                    bg="gray.50"
                    borderRadius="0.375rem"
                    border="1px solid"
                    borderColor="gray.200"
                    minH="3rem"
                  >
                    <Text
                      fontSize="0.875rem"
                      fontWeight="600"
                      color="gray.700"
                      minW="8rem"
                      flexShrink={0}
                      whiteSpace="nowrap"
                    >
                      Lobby Closure Hours Time:
                    </Text>
                    <Flex gap="0.5rem" alignItems="center" flex="1">
                      <ReactSelect
                        options={lobbyClosureTimeOptions}
                        value={
                          lobbyClosureTimeOptions.find(
                            (option) =>
                              option.value === lunchHours.startDisplay
                          ) || null
                        }
                        onChange={(option) => {
                          if (option) {
                            const time12h = option.value;
                            const val24h = to24h(time12h);

                            // Validate that start time is before end time
                            let hasError = false;
                            if (lunchHours.end) {
                              const startMinutes =
                                parseInt(val24h.split(':')[0]) * 60 +
                                parseInt(val24h.split(':')[1]);
                              const endMinutes =
                                parseInt(
                                  lunchHours.end.split(':')[0]
                                ) *
                                  60 +
                                parseInt(
                                  lunchHours.end.split(':')[1]
                                );
                              hasError = startMinutes >= endMinutes;
                            }

                            const next = {
                              ...lunchHours,
                              start: val24h,
                              startDisplay: time12h,
                            };
                            setLunchHours(next);

                            // Clear or set error
                            if (hasError) {
                              // Error handling now managed by validation
                            } else {
                              // Error handling now managed by validation
                            }
                          } else {
                            // Clear the start time when option is null (cross button clicked)
                            const next = {
                              ...lunchHours,
                              start: '',
                              startDisplay: '',
                            };
                            setLunchHours(next);
                          }
                        }}
                        placeholder="Start time"
                        isSearchable={true}
                        isClearable={true}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            minHeight: '32px',
                            minWidth: '110px',
                            width: 'auto',
                            background: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '0.375rem',
                            boxShadow: 'none',
                            fontSize: '0.875rem',
                            '&:hover': {
                              border: '1px solid #cbd5e0',
                            },
                            '&:focus-within': {
                              border: '1px solid #3182ce',
                              boxShadow: '0 0 0 1px #3182ce',
                            },
                          }),
                          menu: (provided) => ({
                            ...provided,
                            zIndex: 9999,
                            fontSize: '0.875rem',
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected
                              ? '#3182ce'
                              : state.isFocused
                              ? '#e6f3ff'
                              : 'white',
                            color: state.isSelected
                              ? 'white'
                              : '#2d3748',
                            fontSize: '0.875rem',
                            padding: '0.5rem 0.75rem',
                            '&:hover': {
                              backgroundColor: state.isSelected
                                ? '#3182ce'
                                : '#e6f3ff',
                            },
                          }),
                          singleValue: (provided) => ({
                            ...provided,
                            color: '#2d3748',
                            fontWeight: '500',
                            fontSize: '0.875rem',
                          }),
                          placeholder: (provided) => ({
                            ...provided,
                            color: '#a0aec0',
                            fontSize: '0.875rem',
                          }),
                          input: (provided) => ({
                            ...provided,
                            fontSize: '0.875rem',
                          }),
                          indicatorSeparator: (provided) => ({
                            ...provided,
                            display: 'none',
                          }),
                          dropdownIndicator: (provided) => ({
                            ...provided,
                            color: '#a0aec0',
                            '&:hover': {
                              color: '#4a5568',
                            },
                          }),
                        }}
                      />
                      <Text
                        fontSize="0.875rem"
                        fontWeight="500"
                        color="gray.600"
                        flexShrink="0"
                        px="0.25rem"
                      >
                        -
                      </Text>
                      <ReactSelect
                        options={lobbyClosureTimeOptions}
                        value={
                          lobbyClosureTimeOptions.find(
                            (option) =>
                              option.value === lunchHours.endDisplay
                          ) || null
                        }
                        onChange={(option) => {
                          if (option) {
                            const time12h = option.value;
                            const val24h = to24h(time12h);

                            // Validate that start time is before end time
                            let hasError = false;
                            if (lunchHours.start) {
                              const startMinutes =
                                parseInt(
                                  lunchHours.start.split(':')[0]
                                ) *
                                  60 +
                                parseInt(
                                  lunchHours.start.split(':')[1]
                                );
                              const endMinutes =
                                parseInt(val24h.split(':')[0]) * 60 +
                                parseInt(val24h.split(':')[1]);
                              hasError = startMinutes >= endMinutes;
                            }

                            const next = {
                              ...lunchHours,
                              end: val24h,
                              endDisplay: time12h,
                            };
                            setLunchHours(next);

                            // Clear or set error
                            if (hasError) {
                              // Error handling now managed by validation
                            } else {
                              // Error handling now managed by validation
                            }
                          } else {
                            // Clear the end time when option is null (cross button clicked)
                            const next = {
                              ...lunchHours,
                              end: '',
                              endDisplay: '',
                            };
                            setLunchHours(next);
                          }
                        }}
                        placeholder="End time"
                        isSearchable={true}
                        isClearable={true}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            minHeight: '32px',
                            minWidth: '110px',
                            width: 'auto',
                            background: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '0.375rem',
                            boxShadow: 'none',
                            fontSize: '0.875rem',
                            '&:hover': {
                              border: '1px solid #cbd5e0',
                            },
                            '&:focus-within': {
                              border: '1px solid #3182ce',
                              boxShadow: '0 0 0 1px #3182ce',
                            },
                          }),
                          menu: (provided) => ({
                            ...provided,
                            zIndex: 9999,
                            fontSize: '0.875rem',
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected
                              ? '#3182ce'
                              : state.isFocused
                              ? '#e6f3ff'
                              : 'white',
                            color: state.isSelected
                              ? 'white'
                              : '#2d3748',
                            fontSize: '0.875rem',
                            padding: '0.5rem 0.75rem',
                            '&:hover': {
                              backgroundColor: state.isSelected
                                ? '#3182ce'
                                : '#e6f3ff',
                            },
                          }),
                          singleValue: (provided) => ({
                            ...provided,
                            color: '#2d3748',
                            fontWeight: '500',
                            fontSize: '0.875rem',
                          }),
                          placeholder: (provided) => ({
                            ...provided,
                            color: '#a0aec0',
                            fontSize: '0.875rem',
                          }),
                          input: (provided) => ({
                            ...provided,
                            fontSize: '0.875rem',
                          }),
                          indicatorSeparator: (provided) => ({
                            ...provided,
                            display: 'none',
                          }),
                          dropdownIndicator: (provided) => ({
                            ...provided,
                            color: '#a0aec0',
                            '&:hover': {
                              color: '#4a5568',
                            },
                          }),
                        }}
                      />
                    </Flex>
                  </Flex>

                  <FormControl
                    isInvalid={officeHoursValidation.errors.some(
                      (e) => e.field.includes('lunch')
                    )}
                  >
                    <FormHelperText
                      fontSize="0.75rem"
                      color="gray.600"
                      mt="0.25rem"
                      ml="0"
                    >
                      Select start and end times for lobby closure
                      hours from the dropdown menus above. Lobby
                      closure hours must fit within the open hours of
                      at least one day.
                    </FormHelperText>
                    <FormErrorMessage
                      fontSize="0.875rem"
                      fontWeight="500"
                    >
                      {officeHoursValidation.errors
                        .filter((e) => e.field.includes('lunch'))
                        .map((e) => e.message)
                        .join('; ')}
                    </FormErrorMessage>
                  </FormControl>
                </>
              )}
            </Flex>

            <OfficeHoursInputComponent
              officeHoursInput={officeHoursInput}
              setOfficeHoursInput={(value) => {
                if (typeof value === 'function') {
                  setOfficeHoursInput(value(officeHoursInput));
                } else {
                  setOfficeHoursInput(value);
                }
              }}
              setValue={setValue}
              to24h={to24h}
              lunchHours={lunchHours}
              observesLunchHours={observesLunchHours}
            />

            {/* Office Hours Preview */}
            <Box
              mt="1rem"
              p="1rem"
              bg="gray.50"
              borderRadius="0.5rem"
              border="1px solid"
              borderColor="gray.200"
            >
              <Flex alignItems="center" gap="0.5rem" mb="0.75rem">
                <Text
                  fontSize="0.875rem"
                  fontWeight="600"
                  color="gray.700"
                >
                  Preview: How your practice will appear
                </Text>
                <Tooltip
                  label="This shows how your practice status will be displayed to users based on your current office hours and lobby closure settings"
                  placement="top"
                  hasArrow
                >
                  <IconButton
                    aria-label="Preview info"
                    icon={<InfoIcon />}
                    size="xs"
                    variant="ghost"
                    colorScheme="gray"
                  />
                </Tooltip>
              </Flex>

              <OfficeHoursPreview
                officeHoursInput={officeHoursInput}
                lunchHours={lunchHours}
                observesLunchHours={observesLunchHours}
                timezone={String(
                  getValues('timezone') || 'America/New_York'
                )}
                state={String(getValues('state') || '')}
              />
            </Box>

            {/* <FormControl isInvalid={!!officeHoursError}>
              <FormErrorMessage>{officeHoursError}</FormErrorMessage>
            </FormControl> */}

            {/* Holidays Section */}
            {/* <Text fontSize="1rem" fontWeight="600">
              Holidays
            </Text>
            {holidays.map((holiday) => (
              <Checkbox
                key={holiday}
                {...register('Holidays')}
                value={holiday}
              >
                {holiday}
              </Checkbox>
            ))} */}

            {/* Medical Services Section */}
            <Text
              fontSize="1.25rem"
              fontWeight="700"
              color="text.secondary"
            >
              Medical Services
            </Text>

            <FormControl>
              <FormLabel
                fontSize={'0.75rem'}
                fontWeight={500}
                lineHeight={'1rem'}
              >
                Screen for Covid When Scheduling
              </FormLabel>
              <HStack>
                <Controller
                  name="Screen-For-Covid-When-Scheduling"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Switch
                        isChecked={field.value === 'Yes'}
                        onChange={(e) =>
                          field.onChange(
                            e.target.checked ? 'Yes' : 'No'
                          )
                        }
                      />
                      <Text>
                        {field.value === 'Yes' ? 'Yes' : 'No'}
                      </Text>
                    </>
                  )}
                />
              </HStack>
            </FormControl>
            <FormControl>
              <FormLabel
                fontSize={'0.75rem'}
                fontWeight={500}
                lineHeight={'1rem'}
              >
                {fieldLabels['Flu-Shots']}
              </FormLabel>
              <Controller
                name="Flu-Shots"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select option"
                    value={getSafeSelectValue(field.value)}
                  >
                    {customFields
                      .find((f) => f.name === field.name)
                      ?.allowed_values?.map((opt) => (
                        <option key={opt.id} value={opt.value}>
                          {opt.value}
                        </option>
                      ))}
                  </Select>
                )}
              />
            </FormControl>
            <FormControl>
              <FormLabel
                fontSize={'0.75rem'}
                fontWeight={500}
                lineHeight={'1rem'}
              >
                {fieldLabels['Ekg']}
              </FormLabel>
              <Controller
                name="Ekg"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Select option"
                    value={getSafeSelectValue(field.value)}
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Select>
                )}
              />
            </FormControl>
            {[
              {
                fieldKey: 'Labs-Drawn-Hours',
                helperText:
                  'Choose Other to write when labs are drawn, e.g. Mon-Wed 8:00 AM to 12:00 PM.',
                detailPlaceholder:
                  'e.g. Mon-Wed 8:00 AM to 12:00 PM',
              },
              {
                fieldKey: 'Imaging-Available-Hours',
                helperText:
                  'Choose Other to write imaging hours, or the name and number to call when imaging is in the building but not in the clinic.',
                detailPlaceholder:
                  'e.g. Mon-Fri 9:00 AM to 4:00 PM',
              },
            ].map(({ fieldKey, helperText, detailPlaceholder }) => (
              <FormControl key={fieldKey}>
                <FormLabel
                  fontSize={'0.75rem'}
                  fontWeight={500}
                  lineHeight={'1rem'}
                >
                  {fieldLabels[fieldKey]}
                </FormLabel>
                <Controller
                  name={fieldKey}
                  control={control}
                  render={({ field }) => (
                    <>
                      <Select
                        {...field}
                        placeholder="Select option"
                        value={getSafeSelectValue(field.value)}
                      >
                        {LAB_IMAGING_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </Select>
                      {field.value === 'Other' && (
                        <Input
                          mt="0.5rem"
                          {...register(
                            labImagingDetailKey(fieldKey)
                          )}
                          placeholder={detailPlaceholder}
                        />
                      )}
                    </>
                  )}
                />
                <FormHelperText fontSize="0.75rem" color="gray.600">
                  {helperText}
                </FormHelperText>
              </FormControl>
            ))}

            {/* Contact Section */}
            <Text
              fontSize="1.25rem"
              fontWeight="700"
              color="text.secondary"
            >
              Contact
            </Text>
            <Flex wrap="wrap" gap="1rem">
              {[
                'Phone',
                'Fax',
                'Medical-Records-Number',
                'Backline',
                'Manager-Name',
                'Manager-Line',
                'Manager-Email',
                'After-Hours',
                'Clinical-Staff',
                'Front-Staff',
                'Director',
              ].map((field) => (
                <FormControl key={field} width="48%">
                  <FormLabel
                    fontSize={'0.75rem'}
                    fontWeight={500}
                    lineHeight={'1rem'}
                  >
                    {fieldLabels[field]}
                  </FormLabel>
                  <Input
                    {...register(field)}
                    placeholder={`Enter ${fieldLabels[field]}`}
                  />
                </FormControl>
              ))}
            </Flex>

            {/* Cerner Information Section */}
            <Text
              fontSize="1.25rem"
              fontWeight="700"
              color="text.secondary"
            >
              Cerner Information
            </Text>
            <Flex wrap="wrap" gap="1rem">
              {['Cerner-pool', 'Cerner-location', 'Cerner-Hub'].map(
                (field) => (
                  <FormControl key={field} width="48%">
                    <FormLabel
                      fontSize={'0.75rem'}
                      fontWeight={500}
                      lineHeight={'1rem'}
                    >
                      {fieldLabels[field] ||
                        field
                          .replace(/-/g, ' ')
                          .replace(/\b\w/g, (c) => c.toUpperCase())}
                    </FormLabel>
                    <Input
                      {...register(field)}
                      placeholder={`Enter ${
                        fieldLabels[field] || field.replace(/-/g, ' ')
                      }`}
                    />
                  </FormControl>
                )
              )}
            </Flex>

            {/* Submit Button */}
            <Flex
              gap="2rem"
              mt="2rem"
              mb="3rem"
              align="left"
              alignItems={'center'}
              justifyContent={'flex-start'}
            >
              <Button
                variant="outline"
                colorScheme="red"
                height={'3rem'}
                borderRadius={'0.375rem'}
                width="7.38rem"
                onClick={cancelDialog.onOpen}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                p="0 1rem"
                height={'3rem'}
                borderRadius={'0.375rem'}
                background={'background.brandLight'}
                width="7.38rem"
                isLoading={isSubmitting}
                loadingText="Saving"
              >
                Save Practice
              </Button>
            </Flex>
          </Flex>
        </form>
        <AlertDialog
          isOpen={cancelDialog.isOpen}
          leastDestructiveRef={cancelRef}
          onClose={cancelDialog.onClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Discard Changes?
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to cancel? You might lose your
                changes.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button
                  ref={cancelRef}
                  onClick={cancelDialog.onClose}
                >
                  No
                </Button>
                <Button
                  colorScheme="red"
                  onClick={() => navigate(`/practices/${practiceId}`)}
                  ml={3}
                >
                  Yes, Cancel
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Flex>
    </>
  );
};

export default CreatePractice;
